import React from 'react';
import { View, Text, StyleSheet, TextInput, Platform, ActivityIndicator, Pressable } from 'react-native';
import { useAppDispatch, useAppSelector } from '@/store';
import { sendInvitation, fetchInvitations } from '@/store/slices/residents/invitationsSlice';
import { useToast } from '@/contexts/admin/ToastContext';
import { newInvitationStrings, formatString } from '@/constants/residents/strings';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import RNButton from '@/components/common/residents/desktop/Button';

type DesktopInviteFormModalProps = {
  visible: boolean;
  onClose: () => void;
};

export const DesktopInviteFormModal: React.FC<DesktopInviteFormModalProps> = ({ visible, onClose }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const { isLoading } = useAppSelector((s) => s.invitations);
  const { showSuccess, showError } = useToast();

  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [infoAnchor, setInfoAnchor] = React.useState<{ x: number; y: number; w: number; h: number } | null>(null);

  React.useEffect(() => {
    const fullName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || '';
    setMessage(formatString(newInvitationStrings.messageFormatted, fullName));
  }, [user]);

  if (Platform.OS !== 'web' || !visible) return null;

  const onSubmit = async () => {
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedEmail) {
      showError('Please enter an email address');
      return;
    }
    if (!trimmedEmail.includes('@')) {
      showError('Please enter a valid email address');
      return;
    }
    if (!trimmedMessage) {
      showError('Please enter a message');
      return;
    }

    try {
      await dispatch(sendInvitation({ email: trimmedEmail, message: trimmedMessage })).unwrap();
      showSuccess('Invitation sent successfully');
      onClose();
      // Refresh list to show latest
      dispatch(fetchInvitations());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send invitation';
      showError(message);
    }
  };

  const overlay = (
    <View style={styles.overlay}>
      <View style={styles.modal}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>{newInvitationStrings.title}</Text>
          <Pressable onPress={onClose} style={styles.closeBtn} accessibilityRole="button" accessibilityLabel="Close">
            <Text style={styles.closeBtnText}>✕</Text>
          </Pressable>
        </View>
        <Text style={styles.headerSubtitle}>{newInvitationStrings.header}</Text>

        <View style={styles.formGroup}>
          <Text style={styles.label}>{newInvitationStrings.emailPlaceholder}</Text>
          <TextInput
            style={styles.input}
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="email"
            placeholder={newInvitationStrings.emailPlaceholder}
            placeholderTextColor={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY}
          />
        </View>

        <View style={styles.formGroup}>
          <View style={styles.labelRow}>
            <Text style={styles.label}>Personal Message</Text>
            <Text style={styles.optional}>(optional)</Text>
          </View>
          <TextInput
            style={[styles.input, styles.textarea]}
            value={message}
            onChangeText={setMessage}
            multiline
            numberOfLines={6}
            textAlignVertical="top"
            placeholder="Enter your message..."
            placeholderTextColor={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY}
          />
        </View>

        {/* How it works (matches resio-web content) */}
        <View style={styles.howSection}>
          <View style={styles.howItem}>
            <Text style={styles.howItemDetail}>{newInvitationStrings.howItWorks.item1.detail}</Text>
          </View>
          <View style={[styles.howItem, styles.howTitleRow]}>
            <Text style={styles.howItemTitle}>{newInvitationStrings.howItWorks.item2.title}</Text>
            <Pressable
              onHoverIn={(e) => {
                const target: any = (e as any)?.nativeEvent?.target;
                if (target && target.getBoundingClientRect) {
                  const rect = target.getBoundingClientRect();
                  setInfoAnchor({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
                }
                setInfoOpen(true);
              }}
              onHoverOut={() => setInfoOpen(false)}
              onPressIn={() => setInfoOpen((v) => !v)}
              style={styles.infoIconBtn}
              accessibilityRole="button"
              accessibilityLabel="More info"
            >
              <RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY} />
            </Pressable>
          </View>
          {/* Show inline detail on non-web platforms as a fallback */}
          {Platform.OS !== 'web' && (
            <Text style={styles.howItemDetail}>{newInvitationStrings.howItWorks.item2.detail}</Text>
          )}
          <AnchoredDropdown isOpen={infoOpen} anchor={infoAnchor} width={360} onRequestClose={() => setInfoOpen(false)}>
            <View style={styles.tooltipBody}>
              <Text style={styles.tooltipText}>{newInvitationStrings.howItWorks.item2.detail}</Text>
            </View>
          </AnchoredDropdown>
        </View>

        <View style={styles.actions}>
          <RNButton title="Cancel" variant="link" onPress={onClose} textStyle={styles.cancelBtnText} />
          <RNButton title={isLoading ? 'Sending...' : newInvitationStrings.buttonTitle} onPress={onSubmit} disabled={isLoading} />
        </View>
      </View>
    </View>
  );

  try {
    const g: any = globalThis as any;
    const ReactDOM = g?.require ? g.require('react-dom') : require('react-dom');
    const target = g?.document?.body;
    if (ReactDOM?.createPortal && target) {
      return ReactDOM.createPortal(overlay, target);
    }
  } catch {}

  return overlay;
};

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 16,
    width: '100%',
    height: '100%',
  },
  modal: {
    width: '100%',
    maxWidth: 560,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#2f2f2f',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 18,
    fontWeight: '700',
  },
  headerSubtitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
    marginTop: 8,
    marginBottom: 16,
  },
  closeBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  closeBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 18,
  },
  formGroup: {
    marginBottom: 14,
  },
  label: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 6,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    gap: 8,
  },
  optional: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 12,
  },
  input: {
    borderWidth: 1,
    borderColor: '#2f2f2f',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  textarea: {
    minHeight: 140,
  },
  howSection: {
    marginTop: 8,
    marginBottom: 8,
  },
  howTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  howTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
    marginBottom: 8,
  },
  howItem: {
    marginBottom: 8,
  },
  howItemTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '600',
    marginBottom: 4,
  },
  howItemDetail: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    lineHeight: 18,
  },
  infoIconBtn: {
    padding: 6,
  },
  howHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  tooltipBody: {
    padding: 12,
  },
  tooltipText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    lineHeight: 18,
  },
  actions: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
});

export default DesktopInviteFormModal;


